import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'

const translate = t as (k: string, options?: unknown) => string

/**
 * i18next escapes interpolated values for HTML by default, but these strings render as
 * React text, which escapes them itself — so a name like "D'Arcy" came out as
 * "D&#39;Arcy". Interpolate the raw value and leave the escaping to React.
 */
const RAW_INTERPOLATION = { interpolation: { escapeValue: false } }

/**
 * Title-cased i18n label. The i18n module is untyped JS whose inferred t()
 * signature demands an options argument — this wrapper keeps call sites clean
 * and applies the house Title Case rule in one place. Use for short labels /
 * action strings only (kebab actions, buttons, status tags).
 */
export const balanceLabel = (
  key: string,
  params?: Record<string, unknown>
): string =>
  capitalizeWords(translate(key, { ...params, ...RAW_INTERPOLATION }))

/**
 * Raw i18n text (NOT Title-cased) with optional interpolation — for sentences and
 * body copy where Title Case would be wrong.
 */
export const balanceText = (
  key: string,
  params?: Record<string, unknown>
): string => translate(key, { ...params, ...RAW_INTERPOLATION })

const BILLING_TYPE_KEYS: Record<string, string> = {
  deposit: 'common:balanceBillingTypeDeposit',
  installment: 'common:balanceBillingTypeInstallment',
  full: 'common:balanceBillingTypeFull',
  adhoc: 'common:balanceBillingTypeAdhoc',
  complimentary: 'common:balanceBillingTypeComplimentary',
  // SN-8310 — a rep's Add Invoice on an invoiced player's share.
  additional: 'common:balanceBillingTypeAdditional',
}

/**
 * Localized, Title-cased billing-type label for the balance surfaces (the payments
 * drawer and both offline-payment previews). Kept apart from the shared
 * getBillingTypeLabel, which every other billing surface uses in English; an
 * unknown type reads as an installment, like that one.
 */
export const billingTypeLabel = (paymentType?: string | null): string =>
  balanceLabel(
    BILLING_TYPE_KEYS[paymentType ?? ''] ??
      'common:balanceBillingTypeInstallment'
  )

const FREQUENCY_KEYS: Record<string, string> = {
  weekly: 'common:installmentFrequencyWeekly',
  'bi-weekly': 'common:installmentFrequencyBiWeekly',
  monthly: 'common:installmentFrequencyMonthly',
  'bi-monthly': 'common:installmentFrequencyBiMonthly',
  quarterly: 'common:installmentFrequencyQuarterly',
  'semi-annually': 'common:installmentFrequencySemiAnnually',
  annually: 'common:installmentFrequencyAnnually',
  // SN-8310 — "Billed on custom dates", never the raw "Billed custom".
  custom: 'common:installmentFrequencyCustom',
}

/**
 * Localized installment-frequency label from the division's config value, or null
 * when the frequency is absent/unknown (fall back to the raw value if unmapped).
 */
export const frequencyLabel = (
  frequency: string | null | undefined
): string | null => {
  if (!frequency) {
    return null
  }
  const key = FREQUENCY_KEYS[frequency]

  return key ? balanceText(key) : frequency
}
